import React, { useEffect, useMemo, useState } from "react";
// eslint-disable-next-line import/no-unresolved
import { Box, Button, DirectDebitList, Text } from "@ui-kit/components/index.es";
import Head from "pages/_components/Head";
import Notification from "pages/_components/Notification";
import { resizableRoute } from "pages/_components/Resizable";
import NewDirectDebitSummaryData from "pages/v2/directDebit/NewDirectDebitSummaryData";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { compose } from "redux";
import { push } from "react-router-redux";
import { actions as accountsActions, selectors as accountsSelectors } from "reducers/accounts";
import { actions as directDebitActions, selectors as directDebitSelectors } from "reducers/directDebit";
import { actions as formActions } from "reducers/form";
import { selectors as sessionSelectors } from "reducers/session";
import { actions as statusActions } from "reducers/status";
import * as accountUtils from "util/accounts";
import * as configUtils from "util/config";
import * as i18n from "util/i18n";
import Yup from "yup";
import { useEmbeddedV3ShellLayoutSync } from "util/v3ShellLayoutSync";

const ListDirectDebit = (props) => {
    const [listDirectDebit, setListDirectDebit] = useState([]);
    const [filterValues, setFilterValues] = useState({});

    const {
        isDesktop,
        dispatch,
        activeEnvironment,
        directDebit,
        totalPages,
        pageNumber,
        accounts,
        loggedUser,
        isFetching,
        embeddedInV3Shell,
        location,
    } = props;

    const listShellRightContentMobile = useMemo(() => {
        if (!embeddedInV3Shell || !activeEnvironment?.permissions["directDebit.manage"]) {
            return null;
        }
        return (
            <Button
                icon="AddCircle"
                iconVariant="outline"
                variant="text"
                color="white"
                onClick={() => {
                    dispatch(push("/v2/directDebit/create"));
                }}
                size="large"
                aria-label={i18n.get("button.directDebit.request")}
            />
        );
    }, [embeddedInV3Shell, activeEnvironment?.permissions, dispatch]);

    useEmbeddedV3ShellLayoutSync(dispatch, {
        embeddedInV3Shell,
        isDesktop,
        titleKey: "directDebit.title",
        goBackPath: "/v3/dashboard",
        scopes: ["directDebit"],
        rightContentMobile: listShellRightContentMobile,
    });

    useEffect(() => {
        dispatch(directDebitActions.directDebitCleanData());
        dispatch(directDebitActions.listDirectDebit());
        if (!accounts?.length > 0) {
            dispatch(accountsActions.listAccounts(loggedUser?.userId));
        }
        if (location) {
            dispatch(statusActions.saveLastHref(location));
        }
    }, []);

    useEffect(() => {
        if (accounts.length > 0) {
            const directDebitWithAccountNumber = directDebit.map((debit) => {
                const account = accounts.find((acc) => acc.cbu === debit.accountNumberId);
                return {
                    ...debit,
                    accountNumber: account ? accountUtils.getAccountName(account) : "",
                };
            });
            setListDirectDebit(directDebitWithAccountNumber);
        } else {
            setListDirectDebit(directDebit);
        }
    }, [accounts, directDebit]);

    const handleBack = () => dispatch(push(embeddedInV3Shell ? "/v3/dashboard" : "/desktop"));

    const cancelTransaction = (transaction) => {
        const cancelTransactionData = <NewDirectDebitSummaryData isDesktop={isDesktop} />;
        dispatch(push({ pathname: "/transactions/cancel" }));
        dispatch(
            formActions.cancelTransactionData({
                idTransaction: transaction.idTransaction,
                content: cancelTransactionData,
                title: i18n.get("directDebit.success.title"),
                force: true,
            }),
        );

        dispatch(directDebitActions.directDebitTransactionSetData(transaction));
        dispatch(directDebitActions.setGoBackDirectDebit(true));
        dispatch(formActions.cancelTransactionPre({ idActivity: transaction.idActivity, idForm: "" }));
    };

    const getCardLabel = (id) => {
        const cardLabels = {
            loadDate: i18n.get("directDebit.list.loadDate.label"),
            fileReference: i18n.get("directDebit.list.fileReference.label"),
            requestId: i18n.get("directDebit.list.requestId.label"),
            accountNumber: i18n.get("directDebit.list.accountNumber.label"),
            amount: i18n.get("directDebit.list.amount.label"),
            status: i18n.get("directDebit.list.status.label"),
            ordersCount: i18n.get("directDebit.list.ordersCount.label"),
            FINISHED: i18n.get("directDebit.detail.status.FINISHED"),
            PENDING: i18n.get("directDebit.detail.status.PENDING"),
            PROCESSED_WITH_SUCCESS: i18n.get("directDebit.detail.status.PROCESSED_WITH_SUCCESS"),
            PROCESSED_WITH_ERRORS: i18n.get("directDebit.detail.status.PROCESSED_WITH_ERRORS"),
            CANCELLED: i18n.get("directDebit.detail.status.CANCELLED"),
            PROCESSING: i18n.get("directDebit.detail.status.PROCESSING"),
        };
        return cardLabels[id];
    };
    const getContextMenuLabel = (action) => {
        const contextLabels = {
            cancel: "Anular",
            sign: "Firmar",
            download: "Descargar detalle",
        };
        return contextLabels[action];
    };
    const contextMenuAction = (id, type) => {
        const transaction = listDirectDebit.find((objDirectDebit) => objDirectDebit.id.toString() === id)?.transaction;
        if (type === "cancel") {
            cancelTransaction(transaction);
        }
        if (type === "sign") {
            dispatch(push(`/transaction/${transaction?.idTransaction}`));
        }
    };
    const emptyStateText = i18n.get("directDebit.list.emptyStateText");
    const paginationProps = {
        pageNumber,
        totalPages,
        action: (n) => {
            dispatch(directDebitActions.listDirectDebit({ ...filterValues, pageNumber: n }, isDesktop));
        },
    };
    const getFormLabel = (id) => {
        const formLabels = {
            status: i18n.get("directDebit.list.filters.status"),
            amountFrom: i18n.get("directDebit.list.filters.amountFrom"),
            amountTo: i18n.get("directDebit.list.filters.amountTo"),
            loadDates: i18n.get("directDebit.list.filters.loadDates"),
            requestId: i18n.get("directDebit.list.filters.requestId"),
            requestIdPlaceholder: i18n.get("directDebit.list.filters.requestIdPlaceholder"),
            fileReference: i18n.get("directDebit.list.filters.fileReference"),
            fileReferencePlaceholder: i18n.get("directDebit.list.filters.fileReferencePlaceholder"),
            filter: i18n.get("directDebit.list.filters.filter"),
            "see.filters": i18n.get("directDebit.list.filters.see"),
            "hide.filters": i18n.get("directDebit.list.filters.hide"),
            "active.filters": i18n.get("directDebit.list.filters.active"),
            "clean.filters": i18n.get("directDebit.list.filters.clean"),
        };
        return formLabels[id];
    };
    const getDefaultDateRange = () => {
        const minDateFrom = configUtils.getInteger("backend.Months.Period.Allowed", 6);
        const sixMonthsAgo = new Date(new Date().setMonth(new Date().getMonth() - minDateFrom));
        const today = new Date();
        return [sixMonthsAgo, today];
    };

    const initialValues = {
        status: { value: "ALL", label: "Todos" },
        currency: null,
        loadDates: getDefaultDateRange(),
        requestId: "",
        fileReference: "",
        amountFrom: 0,
        amountTo: 0,
    };
    const onSubmit = (values) => {
        dispatch(directDebitActions.listDirectDebit({ ...values, pageNumber: 1 }, isDesktop));
        setFilterValues(values);
    };
    const orderByProps = {
        propList: [
            { value: "loadDate", label: "Fecha de creación" },
            { value: "amount", label: "Monto" },
        ],
        defaultValueSelector: "loadDate",
        defaultValueRadioButtons: "descendent",
        onChange: (values) => {
            dispatch(directDebitActions.listDirectDebit(values, isDesktop));
            setFilterValues(values);
        },
    };
    const statusOptions = [
        { value: "FINISHED", label: i18n.get("directDebit.detail.status.FINISHED") },
        { value: "PENDING", label: i18n.get("directDebit.detail.status.PENDING") },
        { value: "PROCESSED_WITH_SUCCESS", label: i18n.get("directDebit.detail.status.PROCESSED_WITH_SUCCESS") },
        { value: "PROCESSED_WITH_ERRORS", label: i18n.get("directDebit.detail.status.PROCESSED_WITH_ERRORS") },
        { value: "CANCELLED", label: i18n.get("directDebit.detail.status.CANCELLED") },
        { value: "PROCESSING", label: i18n.get("directDebit.detail.status.PROCESSING") },
    ];
    const minLoadDateFrom = getDefaultDateRange()[0];
    const maxLoadDateTo = getDefaultDateRange()[1];

    const validationSchema = useMemo(
        () =>
            Yup.object().shape({
                fileReference: Yup.string().min(3, `${i18n.get("forms.validation.min.required")} de 3 caracteres`),
                amountFrom: Yup.number().test(
                    "is-less-than-to",
                    i18n.get("directDebit.list.filters.amountFrom.error"),
                    // eslint-disable-next-line func-names
                    function(value) {
                        // eslint-disable-next-line react/no-this-in-sfc
                        let { amountTo } = this.parent;
                        // eslint-disable-next-line no-restricted-globals
                        if (amountTo === "" || amountTo === null || amountTo === undefined || isNaN(amountTo)) {
                            amountTo = 0;
                        }
                        return (value || 0) <= amountTo;
                    },
                ),

                amountTo: Yup.number().test(
                    "is-more-than-from",
                    i18n.get("directDebit.list.filters.amountTo.error"),
                    // eslint-disable-next-line func-names
                    function(value) {
                        // eslint-disable-next-line react/no-this-in-sfc
                        let { amountFrom } = this.parent;
                        // eslint-disable-next-line no-restricted-globals
                        if (amountFrom === "" || amountFrom === null || amountFrom === undefined || isNaN(amountFrom)) {
                            amountFrom = 0;
                        }
                        return (value || 0) >= amountFrom;
                    },
                ),
            }),
        [],
    );

    const handleDownload = (id) => {
        dispatch(
            directDebitActions.downloadDirectDebitDetailFile(
                "pdf",
                listDirectDebit.find((objDirectDebit) => objDirectDebit.id.toString() === id)?.transaction
                    ?.idTransaction,
            ),
        );
    };

    return (
        <>
            {!embeddedInV3Shell && <Notification scopeToShow="directDebit" />}
            <div className={`admin-detail-head direct-debit-list-wrapper ${isDesktop ? "px-4" : "px-3"}`}>
                {isDesktop && <Head onBack={handleBack} />}
                {!(embeddedInV3Shell && !isDesktop) && (
                    <Head
                        accessibilityTextId="directDebit.title"
                        titleClassName={
                            !isDesktop ? "header-mobile-title-background-blue white-space-normal align-center" : ""
                        }
                        headerClassName={
                            !isDesktop ? "blue-main-header-mobile blue-main-title-mobile background-transparent" : ""
                        }
                        navbarClassName={!isDesktop ? "z-index-8" : ""}
                        centerElement={() => (
                            <Text as="h1" className="m-0" align="center">
                                {i18n.get("directDebit.title")}
                            </Text>
                        )}
                        onBack={!isDesktop && handleBack}
                        rightContent={() => {
                            if (activeEnvironment?.permissions["directDebit.manage"]) {
                                return (
                                    <Button
                                        icon="AddCircle"
                                        iconVariant="outline"
                                        onClick={() => {
                                            dispatch(push("/v2/directDebit/create"));
                                        }}
                                        text={isDesktop ? i18n.get("button.directDebit.request") : undefined}
                                        size={isDesktop ? "medium" : "large"}
                                    />
                                );
                            }
                            return null;
                        }}
                    />
                )}
            </div>
            <Box pt={20} px={24}>
                <DirectDebitList
                    getCardLabel={getCardLabel}
                    chargesArray={listDirectDebit}
                    paginationProps={paginationProps}
                    enableContextMenu
                    enabledFilters
                    getContextMenuLabel={getContextMenuLabel}
                    contextMenuAction={contextMenuAction}
                    emptyStateText={emptyStateText}
                    getFormLabel={getFormLabel}
                    initialValues={initialValues}
                    onSubmit={onSubmit}
                    orderByProps={orderByProps}
                    statusOptions={statusOptions}
                    minLoadDateFrom={minLoadDateFrom}
                    maxLoadDateTo={maxLoadDateTo}
                    onClick={isDesktop ? (value) => dispatch(push(`/directDebit/details/${value}`)) : undefined}
                    loading={isFetching}
                    validationSchema={validationSchema}
                    handleDownloadMobile={handleDownload}
                />
            </Box>
        </>
    );
};

ListDirectDebit.propTypes = {
    isDesktop: PropTypes.bool.isRequired,
    dispatch: PropTypes.func.isRequired,
    activeEnvironment: PropTypes.shape({
        permissions: PropTypes.objectOf(PropTypes.bool).isRequired,
    }).isRequired,
    directDebit: PropTypes.arrayOf(PropTypes.object).isRequired,
    totalPages: PropTypes.number.isRequired,
    pageNumber: PropTypes.number.isRequired,
    accounts: PropTypes.arrayOf(PropTypes.object).isRequired,
    loggedUser: PropTypes.shape({
        userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }).isRequired,
    isFetching: PropTypes.bool.isRequired,
    embeddedInV3Shell: PropTypes.bool,
    location: PropTypes.shape({
        pathname: PropTypes.string,
    }),
};

ListDirectDebit.defaultProps = {
    embeddedInV3Shell: false,
    location: null,
};

const mapStateToProps = (state) => ({
    accounts: accountsSelectors.getAccounts(state),
    activeEnvironment: sessionSelectors.getActiveEnvironment(state),
    directDebit: directDebitSelectors.getDirectDebitList(state),
    totalPages: directDebitSelectors.getTotalPages(state),
    pageNumber: directDebitSelectors.getPageNumber(state),
    isFetching: directDebitSelectors.getFetchingList(state),
    loggedUser: sessionSelectors.getUser(state),
});
export default compose(connect(mapStateToProps), resizableRoute)(ListDirectDebit);
